/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { NotificationViewStyle } from '@airasia-phoenix/react-native/components/NotificationView';
import { useCallback } from 'react';
import { useSsoErrorReporter } from './useSsoErrorReporter';
import { SCREEN_NAME } from '../config/analytics';
import { useTranslation } from 'react-i18next';
import { PasswordlessResponse } from '../managers';
import { describeSsoError } from '../utils/ssoErrors';
import { LoginNotification } from '../widgets/LoginView/types';
import { EmailSignUpMode } from '../widgets/SignUpView/types';
import { useCheckIfUserNameExists } from './account';
import { EmailAuthCredentials } from './useEmailAuthFlow';
import { useRequestPasswordlessCode } from './useRequestPasswordlessCode';

export interface UseEmailSignUpSubmitOptions {
  /** Both modes check availability first; `passwordless` then requests a one-time code. */
  emailSignUpMode: EmailSignUpMode;
  onProceedToPersonalDetails?: (data: {
    email: string;
    password: string;
  }) => void;
  onPasswordLessEmailSignUpSuccess?: (data: PasswordlessResponse) => void;
  onSignUpError?: (error: Error) => void;
  /** Owned by the view so every flow shares one notification slot. */
  setNotification: (notification: LoginNotification | null) => void;
}

/** The sign-up screen's submit half — pair with {@link useEmailAuthFlow} for the fields. */
export function useEmailSignUpSubmit(options: UseEmailSignUpSubmitOptions) {
  const {
    emailSignUpMode,
    onProceedToPersonalDetails,
    onPasswordLessEmailSignUpSuccess,
    onSignUpError,
    setNotification,
  } = options;

  const { t } = useTranslation('account');

  const { isPending: isCheckingUsername, mutate: checkUsernameExists } =
    useCheckIfUserNameExists();
  const { isPending: isPasswordlessLoading, mutate: passwordlessLoginMutate } =
    useRequestPasswordlessCode();

  const reportError = useSsoErrorReporter(SCREEN_NAME.signupV2);

  const showError = useCallback(
    (error: Error) => {
      onSignUpError?.(error);
      const { message } = describeSsoError(
        error,
        t,
        t('signUpView.emailCheckFailed'),
      );
      reportError(error, message);
      setNotification({ message, type: NotificationViewStyle.ERROR });
    },
    [onSignUpError, reportError, setNotification, t],
  );

  const submit = useCallback(
    ({ email, password }: EmailAuthCredentials) => {
      if (isCheckingUsername || isPasswordlessLoading || !email) return;

      // Both modes reject an already-registered email before proceeding.
      checkUsernameExists(
        { username: email },
        {
          onSuccess: (isAvailable) => {
            if (!isAvailable) {
              setNotification({
                message: t('signUpView.emailAlreadyRegistered'),
                type: NotificationViewStyle.ERROR,
              });
              return;
            }

            /**
             * PASSWORDLESS FLOW
             */
            if (emailSignUpMode === 'passwordless') {
              passwordlessLoginMutate(
                { username: email },
                {
                  onSuccess: (data) => {
                    setNotification(null);
                    onPasswordLessEmailSignUpSuccess?.({ ...data, email });
                  },
                  onError: showError,
                },
              );
              return;
            }

            /**
             * PASSWORD FLOW
             */
            setNotification(null);
            if (password) {
              onProceedToPersonalDetails?.({ email, password });
            }
          },
          onError: showError,
        },
      );
    },
    [
      emailSignUpMode,
      isCheckingUsername,
      isPasswordlessLoading,
      checkUsernameExists,
      passwordlessLoginMutate,
      onProceedToPersonalDetails,
      onPasswordLessEmailSignUpSuccess,
      setNotification,
      showError,
      t,
    ],
  );

  return {
    submit,
    /** True while either mode's request is in flight. */
    isSignUpPending: isCheckingUsername || isPasswordlessLoading,
  };
}
